import { ChevronRight } from 'lucide-react';
import { useState } from 'react';

import { cn } from '@/lib/shadcn/utils';
import { useUpdateJobAutomationMutation } from '@/modules/jobs/mutations/useUpdateJobAutomationMutation';
import type { JobApi } from '@/modules/jobs/types';
import { Button } from '@/modules/ui/components/button/Button';
import { InlineError } from '@/modules/ui/components/inline-error/InlineError';
import { SavedIndicator } from '@/modules/ui/components/saved-indicator/SavedIndicator';
import { useSavedFlash } from '@/modules/ui/components/saved-indicator/useSavedFlash';
import { Textarea } from '@/modules/ui/components/textarea/Textarea';
import { actionErrorMessage } from '@/utils/actionErrorMessage';

interface DraftTemplateSectionProps {
  job: JobApi;
}

/**
 * The job template Finlay follows, collapsed by default and saved to the job.
 * "Saved" confirms beside the button; a failure stays under the field.
 */
export function DraftTemplateSection({ job }: DraftTemplateSectionProps) {
  const updateAutomation = useUpdateJobAutomationMutation();
  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState<string | null>(null);
  const { isSaved, markSaved, clearSaved } = useSavedFlash();
  const saved = job.initialReachOutMessageTemplate ?? '';
  const current = value ?? saved;
  const isDirty = current !== saved;

  const save = () => {
    clearSaved();
    updateAutomation.mutate(
      {
        jobId: job.id,
        body: {
          // The API stores a blank template as none.
          initialReachOutMessageTemplate: current,
          // Same rule as the job editor: a template means follow it, an
          // empty one hands the wording back to Finlay.
          isMessagingCreative: current.trim() === '',
        },
      },
      {
        onSuccess: () => {
          setValue(null);
          markSaved();
        },
      },
    );
  };

  return (
    <div className="border-t">
      <button
        aria-expanded={isOpen}
        className="hover:bg-accent flex w-full cursor-pointer items-center gap-2 px-6 py-2.5 text-left text-[13px] font-medium"
        onClick={() => setIsOpen((open) => !open)}
        type="button"
      >
        <ChevronRight
          className={cn(
            'size-4 shrink-0 text-neutral-500 transition-transform duration-200 ease-out motion-reduce:transition-none',
            isOpen && 'rotate-90',
          )}
        />
        <span className="whitespace-nowrap">What Finlay is told to write</span>
        {!isOpen && (
          <span className="ml-auto hidden min-w-0 truncate text-xs font-normal text-neutral-500 sm:inline">
            {saved ? saved : 'No template · Finlay writes freely'}
          </span>
        )}
      </button>
      {/* Always mounted so the height can animate (0fr → 1fr); `inert` keeps
          the collapsed textarea out of the tab order. */}
      <div
        className={cn(
          'grid transition-[grid-template-rows,opacity] duration-200 ease-out motion-reduce:transition-none',
          isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
        )}
        inert={!isOpen}
      >
        <div className="min-h-0 overflow-hidden">
          {/* pt-1 leaves room for the textarea focus ring inside overflow-hidden. */}
          <div className="flex flex-col gap-2 px-6 pt-1 pb-4">
            <Textarea
              aria-label="Reach-out template"
              className="min-h-24 leading-6"
              onChange={(event) => {
                setValue(event.target.value);
                clearSaved();
              }}
              placeholder="Optional. The message Finlay bases each draft on. Leave empty to let Finlay write freely."
              value={current}
            />
            <div className="flex items-center justify-between gap-3">
              <p className="text-[13px] text-neutral-500">
                Applies to the next drafts, not the ones above.
              </p>
              {/* Mounted before the save so screen readers announce "Saved". */}
              <div className="flex items-center gap-3">
                <SavedIndicator isSaved={isSaved} />
                {isDirty && (
                  <Button
                    className="h-7 rounded-md"
                    loading={updateAutomation.isPending}
                    onClick={save}
                    size="sm"
                  >
                    Save template
                  </Button>
                )}
              </div>
            </div>
            {updateAutomation.isError && (
              <InlineError>
                {actionErrorMessage(
                  'save the template',
                  updateAutomation.error,
                )}
              </InlineError>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
